import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import { getSafeTextSizeClasses } from '../utils/textSizeClasses';

// TypeScript interfaces
interface NavigationLinksProps {
  textSize: string;
  highContrast: boolean;
  translations: any; // TODO: Define proper translations type
  userRole?: string | null;
  onLogout: () => void;
}

/**
 * NavigationLinks Component
 *
 * Renders navigation links for the header with proper styling and accessibility.
 * Supports both authenticated and unauthenticated states.
 *
 * @param {Object} props - Component props
 * @param {string} props.textSize - Current text size setting ('small', 'medium', 'large')
 * @param {boolean} props.highContrast - Whether high contrast mode is enabled
 * @param {Object} props.translations - Translation object for the current language
 * @param {string|null} props.userRole - Current user role or null if not authenticated
 * @param {Function} props.onLogout - Callback function for logout action
 * @returns {JSX.Element} Navigation links component
 */
const NavigationLinksComponent: React.FC<any> = ({ textSize, highContrast, translations, userRole = null, onLogout }) => {
    // Get safe text size classes
    const sizeClasses = getSafeTextSizeClasses(textSize);
    
    // Main navigation links configuration
    const mainNavigation = [
      { key: 'inicio', to: '/', label: translations.inicio },
      {
        key: 'simulaciones',
        to: '/simulaciones',
        label: translations.simulaciones,
  },
      { key: 'guias', to: '/guias', label: translations.guias },
      { key: 'ayuda', to: '/ayuda', label: translations.ayuda },
      { key: 'aboutUs', to: '/about', label: translations.aboutUs },
    ];

    /**
     * Gets the CSS classes for navigation links based on active state and theme
     * @param {boolean} isActive - Whether the link is currently active
     * @returns {string} CSS classes string
     */
    const getNavLinkClasses = (isActive: boolean) => {
      const baseClasses = `px-6 py-3 ${sizeClasses.button} rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50`;

      if (isActive) {
        return `${baseClasses} ${
          highContrast
            ? 'bg-high-accent text-high-background font-bold'
            : 'bg-orange-300 text-orange-900 font-bold dark:bg-gray-700 dark:text-white'
    }`;
}

      return `${baseClasses} ${
        highContrast
          ? 'text-high-foreground hover:bg-high-accent hover:text-high-background'
          : 'text-gray-700 dark:text-gray-300 hover:bg-orange-200 dark:text-gray-100 dark:hover:bg-gray-700'
  }`;
};

    /**
     * Gets the CSS classes for action buttons (register, profile, logout)
     * @param {string} variant - Button variant ('register', 'profile', 'logout')
     * @returns {string} CSS classes string
     */
    const getActionButtonClasses = (variant: string) => {
      const baseClasses = `px-6 py-3 ${sizeClasses.button} rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-opacity-50`;

      if (highContrast) {
        return `${baseClasses} bg-high-accent text-high-background hover:opacity-80 focus:ring-high-accent`;
}

      switch (variant) {
        case 'register':
          return `${baseClasses} bg-orange-500 text-white hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700 focus:ring-orange-500`;
        case 'profile':
          return `${baseClasses} bg-green-600 text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 focus:ring-green-500`;
        case 'logout':
          return `${baseClasses} bg-red-500 text-white hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 focus:ring-red-500`;
        default:
          return baseClasses;
}
};

    return (
      <>
        {/* Main Navigation Links */}
        {mainNavigation.map(({ key, to, label }) => (
          <NavLink
            key={key}
            to={to}
            className={({ isActive }) => getNavLinkClasses(isActive)}
          >
            {label}
          </NavLink>
        ))}

        {/* Authentication-dependent Links */}
        {!userRole ? (
          // Registration link for unauthenticated users
          <Link
            to='/registration'
            className={getActionButtonClasses('register')}
            aria-label={`${translations.register || undefined} - ${translations.registerDescription || 'Create a new account'}`}
          >
            {translations.register}
          </Link>
        ) : (
          // Profile and logout for authenticated users
          <>
            <Link
              to='/profile'
              className={getActionButtonClasses('profile')}
              aria-label={`${translations.profile || undefined} - ${translations.profileDescription || 'View your profile'}`}
            >
              {translations.profile}
            </Link>
            <button
              onClick={onLogout}
              className={getActionButtonClasses('logout')}
              aria-label={`${translations.logout || undefined} - ${translations.logoutDescription || 'Sign out of your account'}`}
              type='button'
            >
              {translations.logout}
            </button>
          </>
        )}
      </>
    );
};

const NavigationLinks = React.memo(NavigationLinksComponent);

// Display name for debugging
NavigationLinks.displayName = 'NavigationLinks';

export default NavigationLinks;
